// @flow

import OrderSearchEntry from "apps/orders/OrderSearchEntry"
import { autobind } from "core-decorators"
import { observer } from "mobx-react"
import * as React from "react"
import { hot } from "react-hot-loader/root"
import { IntlProvider } from "react-intl"
import Loadable from "react-loadable"
import {
  Redirect,
  Route,
  BrowserRouter as Router,
  Switch
} from "react-router-dom"
import AutoUpdater from "shared/components/AutoUpdater"
import ApiVersionStore from "shared/stores/ApiVersionStore"
import IntlStore from "shared/stores/IntlStore"
import UserStore from "shared/stores/UserStore"
import Fonts from "shared/utils/fonts"
import { createGlobalStyle } from "styled-components"

import AsyncLoading from "./apps/dashboard/AsyncLoading"
import DriverWebOnBoarding from "./apps/driverweb/onboard/index"
import ScrollToTop from "./ScrollToTop"
import UnsupportedBrowser from "./UnsupportedBrowser"

const Dashboard = Loadable({
  loader: () => import("apps/dashboard/Dashboard"),
  loading: AsyncLoading
})
const Login = Loadable({
  loader: () => import("apps/login/Login"),
  loading: AsyncLoading
})

@observer
@autobind
class App extends React.Component<{}> {
  componentDidMount() {
    Fonts()
    ApiVersionStore.setApiVersion()
  }

  renderPrivateRoutes() {
    if (!UserStore.loggedIn) return null

    return <Route path="/dashboard/:app?/:subApp?" component={Dashboard} />
  }

  render() {
    const isIE = !!window.MSInputMethodContext && !!document.documentMode

    if (isIE) {
      return <UnsupportedBrowser />
    }

    const locale = IntlStore.locale

    // Create GlobalStyle inside render to make it hot-reloadable
    const GlobalStyle = createGlobalStyle`
      #root {
        white-space: pre-line;
      }
      .ant-layout.ant-layout-has-sider > .ant-layout{
        min-height: 100vh;
      }
      .ant-layout > .ant-layout-content {
        flex: 1 1 auto;
      }
      
      /* Fix for three dots button border in campaign zones and other areas */
      .ant-btn-circle {
        border: transparent !important;
        display: flex;
        align-items: center;
        justify-content: center;
        min-width: 0;
      }
      .ant-btn.ant-btn-circle {
        border: transparent !important;
        display: flex;
        align-items: center;
        justify-content: center;
        min-width: 0;
      }
      .ant-btn-circle:hover,
      .ant-btn-circle:focus,
      .ant-btn-circle:active {
        border: transparent !important;
        display: flex;
        align-items: center;
        justify-content: center;
        min-width: 0;
      }

      /* Ensure ellipsis icon is properly centered */
      .ant-btn-circle .anticon-ellipsis {
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        height: 100% !important;
        width: 100% !important;
      }
      
      .ant-btn-circle .anticon-ellipsis svg {
        vertical-align: middle !important;
        display: block !important;
      }

      /* Target the specific DotsDropdown container class */
      .DotsDropdown__container__ibptn {
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
      }

      .DotsDropdown__container__ibptn .anticon {
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
      }
    `

    return (
      <IntlProvider locale={locale.name} messages={locale.messages}>
        <GlobalStyle />
        <Router>
          <ScrollToTop>
            <div className={"co-router-container"}>
              <AutoUpdater />
              <Switch>
                {this.renderPrivateRoutes()}
                <Route path="/driver/" component={DriverWebOnBoarding} />
                <Route
                  path="/iframe/order-search/"
                  component={OrderSearchEntry}
                />
                <Route path="/" component={Login} />
                <Route render={() => <Redirect to="/" />} />
              </Switch>
            </div>
          </ScrollToTop>
        </Router>
      </IntlProvider>
    )
  }
}

export default (hot(App): any)
